import React, { useEffect, useState } from "react";
import { IoCloseOutline } from "react-icons/io5";
import CommonTextField from "components/common/CommonTextField";
import CommonDropzone from "components/common/CommonDropzone";
import axios from "axios";
import { UPDATE_ORGANISATION } from "URLRoutes";
import { generateURLOptions } from "URLOptions";

/* ----------------------------
   PLANS (inline)
---------------------------- */
const PLANS = [
  { name: "Mind Pulse Heart", perUserPrice: 0.0 },
  { name: "Mind Pulse Core", perUserPrice: 5.99 },
  { name: "Mind Pulse Expand", perUserPrice: 6.99 },
  { name: "Mind Pulse Optimum", perUserPrice: 7.99 },
];

const EditOrganisationModal = ({
  isOpen,
  onClose,
  organisation,
  onUploadLogo, // async (file) => string (url)
  isUploadingLogo, // boolean
}) => {
  const [companyName, setCompanyName] = useState("");
  const [companyPhone, setCompanyPhone] = useState("");
  const [companyLogo, setCompanyLogo] = useState("");
  const [primaryColor, setPrimaryColor] = useState("#000000");
  const [notificationTime, setNotificationTime] = useState(""); // HH:mm only
  const [seats, setSeats] = useState(3);

  const [planName, setPlanName] = useState("");
  const [perUserPrice, setPerUserPrice] = useState("");
  const [planStartDate, setPlanStartDate] = useState("");

  const [status, setStatus] = useState(true);

  useEffect(() => {
    if (!isOpen || !organisation) return;

    console.log(organisation);

    setCompanyName(organisation.company_name ?? "");
    setCompanyPhone(organisation.company_phone ?? "");
    setCompanyLogo(organisation.company_logo ?? "");
    setPrimaryColor(organisation.company_primary_color ?? "#000000");

    // IMPORTANT: time-only, no timezone conversion
    setNotificationTime(
      organisation.company_notification_time
        ? organisation.company_notification_time.slice(11, 16) // HH:mm
        : "",
    );

    setSeats(organisation.seats ?? 3);
    setPlanName(organisation.planName ?? organisation.companyPlan?.name ?? "");
    setPerUserPrice(
      organisation.perUserPrice !== undefined
        ? String(Number(organisation.perUserPrice).toFixed(2))
        : "0.00",
    );

    setPlanStartDate(
      organisation.companyPlanStartDate
        ? new Date(organisation.companyPlanStartDate).toLocaleDateString(
            "en-CA",
          ) // YYYY-MM-DD format
        : "",
    );
    setStatus(!organisation.status ?? true);
  }, [isOpen, organisation]);

  const handlePlanChange = (value) => {
    const plan = PLANS.find((p) => p.name === value);
    setPlanName(value);
    setPerUserPrice(
      plan?.perUserPrice !== undefined ? String(plan.perUserPrice) : "",
    );
  };

  const handleLogoDrop = async (file) => {
    if (!onUploadLogo) return;
    const url = await onUploadLogo(file);
    if (url) setCompanyLogo(url);
  };

  const handleSave = async () => {
    const response = await axios.post(
      UPDATE_ORGANISATION,
      {
        organisationId: organisation?._id,
        company_name: companyName?.length > 0 ? companyName : null,
        company_phone: companyPhone?.length > 0 ? companyPhone : null,
        company_logo: companyLogo?.length > 0 ? companyLogo : null,
        company_primary_color: primaryColor, // HEX saved
        company_notification_time: notificationTime
          ? `1970-01-01T${notificationTime}:00`
          : null, // time-only, fixed reference date
        seats: Number(seats),
        planName: planName || null,
        perUserPrice: perUserPrice !== "" ? Number(perUserPrice) : null,
        companyPlanStartDate: planStartDate
          ? `${planStartDate}T00:00:00`
          : null,
        status: status == true ? false : true, // Inverted
      },
      {
        headers: generateURLOptions(),
      },
    );

    if (response?.data?.success == false) {
      return alert(response?.data?.message ?? "");
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl p-6 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold">Edit Organisation</h2>
          <button onClick={onClose}>
            <IoCloseOutline size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="space-y-4 overflow-y-auto pr-2">
          <CommonTextField
            label="Company Name"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
          />

          <CommonTextField
            label="Company Phone"
            value={companyPhone}
            onChange={(e) => setCompanyPhone(e.target.value)}
          />

          {/* Logo Upload */}
          <p className="mb-2 font-semibold text-sm">Company Logo</p>
          <div className="flex flex-row justify-center items-center">
            {companyLogo && (
              <img
                src={companyLogo}
                alt="Company Logo"
                className="h-24 w-2/5 mb-3 mr-4 object-contain"
              />
            )}
            <CommonDropzone
              dropFile={handleLogoDrop}
              isUploading={isUploadingLogo}
            />
          </div>

          {/* Primary Color */}
          <div>
            <p className="mb-1 font-semibold text-sm">Primary Brand Color</p>
            <div className="w-full flex items-center gap-3">
              <input
                type="color"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                className="h-12 w-1/2 cursor-pointer rounded-lg"
              />
              <input
                type="text"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                className="border rounded-lg px-4 py-2 text-sm w-1/2 h-12"
              />
            </div>
          </div>

          {/* Notification Time */}
          <CommonTextField
            label="Daily Notification Time"
            type="time"
            value={notificationTime}
            onChange={(e) => setNotificationTime(e.target.value)}
          />

          <CommonTextField
            label="Seats"
            type="number"
            value={seats}
            onChange={(e) => setSeats(e.target.value)}
          />

          {/* Plan */}
          <div>
            <p className="mb-1 font-semibold text-sm">Subscription Plan</p>
            <select
              value={planName}
              onChange={(e) => handlePlanChange(e.target.value)}
              className="w-full border rounded-md px-3 py-2 text-sm"
            >
              <option value="">Select a Plan</option>
              {PLANS.map((plan) => (
                <option key={plan.name} value={plan.name}>
                  {plan.name}
                </option>
              ))}
            </select>
          </div>

          <CommonTextField
            label="Per User Price"
            type="number"
            value={perUserPrice}
            onChange={(e) => setPerUserPrice(e.target.value)}
          />

          <CommonTextField
            label="Plan Start Date"
            type="date"
            value={planStartDate}
            onChange={(e) => setPlanStartDate(e.target.value)}
          />

          {/* Status */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={status}
              onChange={(e) => setStatus(e.target.checked)}
            />
            <span className="text-sm font-medium">Organisation Enabled</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex mt-6 gap-3">
          <button
            onClick={onClose}
            className="w-1/2 bg-gray-200 py-2 rounded-md"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="w-1/2 bg-blue-600 text-white py-2 rounded-md"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditOrganisationModal;
